import {
  queryOptions,
  infiniteQueryOptions,
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";

export interface UnifiedNotification {
  id: number;
  scope: "guild" | "user" | "tenant" | "broadcast";
  type: string;
  title: string;
  message: string | null;
  metadata: Record<string, unknown>;
  isRead: boolean;
  createdAt: string;
  applicationGuildId: string | null;
  userId: string | null;
  tenantId: string | null;
}

export interface UnifiedNotificationPage {
  data: UnifiedNotification[];
  nextCursor: number | null;
  unreadCount: number;
}

interface NotificationPreferencesResponse {
  data: { mutedTypes: string[] };
}

export type NotificationFilter = "all" | "unread";

export const unifiedNotificationsKeys = {
  all: ["unified-notifications"] as const,
  list: (filter: NotificationFilter) =>
    [...unifiedNotificationsKeys.all, "list", filter] as const,
  count: () => [...unifiedNotificationsKeys.all, "count"] as const,
  preferences: () => [...unifiedNotificationsKeys.all, "preferences"] as const,
};

/** Lightweight query for unread count only (used by usePageTitle) */
export const unifiedNotificationsQueryOptions = () =>
  queryOptions({
    queryKey: unifiedNotificationsKeys.count(),
    staleTime: 30_000,
    queryFn: () =>
      apiClient
        .get<UnifiedNotificationPage>("/api/dashboard/notifications", {
          params: { limit: 1 },
        })
        .then((r) => ({ unreadCount: r.data.unreadCount })),
  });

export const unifiedNotificationsInfiniteQueryOptions = (
  filter: NotificationFilter
) =>
  infiniteQueryOptions({
    queryKey: unifiedNotificationsKeys.list(filter),
    queryFn: ({ pageParam }) =>
      apiClient
        .get<UnifiedNotificationPage>("/api/dashboard/notifications", {
          params: {
            filter,
            ...(pageParam ? { cursor: pageParam } : {}),
          },
        })
        .then((r) => r.data),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

export function useUnifiedNotificationsInfiniteQuery(
  filter: NotificationFilter
) {
  return useInfiniteQuery(unifiedNotificationsInfiniteQueryOptions(filter));
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiClient.patch(`/api/dashboard/notifications/${id}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: unifiedNotificationsKeys.all,
      });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.patch("/api/dashboard/notifications/read-all"),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: unifiedNotificationsKeys.all,
      });
    },
  });
}

export function useClearAllNotifications() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.delete("/api/dashboard/notifications"),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: unifiedNotificationsKeys.all,
      });
    },
  });
}

export const notificationPreferencesQueryOptions = () =>
  queryOptions({
    queryKey: unifiedNotificationsKeys.preferences(),
    queryFn: () =>
      apiClient
        .get<NotificationPreferencesResponse>(
          "/api/dashboard/notifications/preferences"
        )
        .then((r) => r.data.data),
  });

export function useNotificationPreferencesQuery() {
  return useQuery(notificationPreferencesQueryOptions());
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (mutedTypes: string[]) =>
      apiClient.patch("/api/dashboard/notifications/preferences", {
        mutedTypes,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: unifiedNotificationsKeys.all,
      });
    },
  });
}
